import React, { useState, useEffect, useCallback } from 'react';
import { informesAPI, clientesAPI, tiposClientesAPI, tiposProveedoresAPI, whatsappBaileysAPI, cajasAPI, cuentasBancariasAPI } from '../services/api';
import { WhatsAppServicioPanel } from '../components/WhatsAppServicioPanel';
import {
  readUseBaileysFromStorage,
  writeUseBaileysToStorage,
  readSendRateProfileFromStorage,
  sendWhatsAppSequential,
  abrirWhatsappWeb,
  abrirWhatsappWebEscalonado,
} from '../utils/whatsappBaileys';
import { assertHorarioUsoCliente } from '../hooks/useHorariosUso';
import { useZonaHoraria } from '../context/ZonaHorariaContext';
import { buildPrintHeaderHtml, estilosPrintHeaderInforme, getPrintLogoUrl, getPrintEncabezado } from '../utils/printBranding';
import { toast } from 'react-toastify';
import { ExportActions } from '../components/ExportActions';
import { exportListado } from '../utils/exportListado';
import '../Clientes.css';
import '../Informes.css';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

const TIPOS_CON_RANGO_FECHA = new Set([
  'cliente',
  'tipo',
  'clientes-saldo',
  'movimientos',
  'formas-pago',
  'evolucion',
  'movimientos-fondos',
  'saldos-fondos',
  'fondos-tipo-proveedor',
  'abonos-cargados-cobrados',
]);

const fmtMoneyInforme = (n) =>
  `$${parseFloat(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatearTelefonoInforme = (telefono) => {
  if (!telefono) return '';
  let n = String(telefono).replace(/[\s\-()]/g, '');
  if (n.startsWith('0')) n = n.substring(1);
  if (!n.startsWith('+') && !n.startsWith('54')) n = '54' + n;
  if (!n.startsWith('+')) n = '+' + n;
  return n;
};

const Informes = () => {
  const { formatFecha, formatFechaHora, fechaHoy } = useZonaHoraria();
  const [tipoInforme, setTipoInforme] = useState('general');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [fechaCierre, setFechaCierre] = useState(() => fechaHoy());
  const [tipoCliente, setTipoCliente] = useState('');
  const [tipoProveedor, setTipoProveedor] = useState('');
  const [clienteId, setClienteId] = useState('');
  const [clientes, setClientes] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [tiposProveedores, setTiposProveedores] = useState([]);
  const [datos, setDatos] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mostrarGraficos, setMostrarGraficos] = useState(true);
  const [clientesSeleccionados, setClientesSeleccionados] = useState([]);
  const [filtrosClientes, setFiltrosClientes] = useState({
    condicion_impositiva: '',
    ingresos_brutos: '',
    drg: '',
    dv: '',
    iva_liquida: '',
  });
  const [waUseBaileys, setWaUseBaileys] = useState(() => readUseBaileysFromStorage());
  const [waSendRateProfile, setWaSendRateProfile] = useState(() => readSendRateProfileFromStorage());
  const [waStatus, setWaStatus] = useState(null);
  const [waStatusLoading, setWaStatusLoading] = useState(false);
  const [cajasSeleccionadas, setCajasSeleccionadas] = useState([]);
  const [cuentasSeleccionadas, setCuentasSeleccionadas] = useState([]);
  const [tituloEntidadFondos, setTituloEntidadFondos] = useState(null);
  const [filtroUnicoFondos, setFiltroUnicoFondos] = useState(false);
  const [cajas, setCajas] = useState([]);
  const [cuentasBancarias, setCuentasBancarias] = useState([]);
  const [saldoInicialFondos, setSaldoInicialFondos] = useState(null);
  const [modalInformeAbierto, setModalInformeAbierto] = useState(false);
  const [drillDownTipo, setDrillDownTipo] = useState(null);
  const [drillDownMovimientos, setDrillDownMovimientos] = useState([]);
  const [drillDownLoading, setDrillDownLoading] = useState(false);
  const [modalDrillDownAbierto, setModalDrillDownAbierto] = useState(false);

  const refreshWaStatus = useCallback(async () => {
    if (!waUseBaileys) return;
    setWaStatusLoading(true);
    try {
      const s = await whatsappBaileysAPI.getStatus();
      setWaStatus(s);
    } catch (e) {
      setWaStatus({ reachable: false, connected: false, serviceError: e.message });
    } finally {
      setWaStatusLoading(false);
    }
  }, [waUseBaileys]);

  const handleToggleWaBaileys = (v) => {
    writeUseBaileysToStorage(v);
    setWaUseBaileys(v);
    if (v) setTimeout(() => refreshWaStatus(), 0);
  };

  useEffect(() => {
    if (tipoInforme === 'clientes-saldo' && waUseBaileys) refreshWaStatus();
  }, [tipoInforme, waUseBaileys, refreshWaStatus]);

  React.useEffect(() => {
    loadClientes();
    loadTipos();
    loadTiposProveedores();
    cajasAPI.getAll({ activo: true }).then((r) => r.success && setCajas(r.data || [])).catch(() => {});
    cuentasBancariasAPI.getAll({ activo: true }).then((r) => r.success && setCuentasBancarias(r.data || [])).catch(() => {});
  }, []);

  const loadClientes = async () => {
    try {
      const response = await clientesAPI.getAll({ activo: true });
      if (response.success) {
        setClientes(response.data);
      }
    } catch (error) {
      console.error('Error al cargar clientes:', error);
    }
  };

  const loadTipos = async () => {
    try {
      const response = await tiposClientesAPI.getAll();
      if (response.success) {
        setTipos(response.data.map(t => ({ id: t.id, nombre: t.nombre })));
      }
    } catch (error) {
      console.error('Error al cargar tipos:', error);
    }
  };

  const loadTiposProveedores = async () => {
    try {
      const response = await tiposProveedoresAPI.getAll();
      if (response.success) {
        setTiposProveedores(response.data.map(t => ({ id: t.id, nombre: t.nombre })));
      }
    } catch (error) {
      console.error('Error al cargar tipos de proveedor:', error);
    }
  };

  const handleGenerarInforme = async () => {
    setLoading(true);
    try {
      let response;
      const params = {};
      if (fechaDesde) params.fecha_desde = fechaDesde;
      if (fechaHasta) params.fecha_hasta = fechaHasta;

      switch (tipoInforme) {
        case 'general':
          response = await informesAPI.resumenGeneral(params);
          break;
        case 'cliente':
          if (!clienteId) {
            toast.error('Seleccione un cliente');
            setLoading(false);
            return;
          }
          response = await informesAPI.resumenCliente(clienteId, params);
          break;
        case 'tipo':
          if (tipoCliente) params.tipo = tipoCliente;
          response = await informesAPI.resumenTipo(params);
          break;
        case 'clientes-saldo':
          if (tipoCliente) params.tipo = tipoCliente;
          if (filtrosClientes.condicion_impositiva) params.condicion_impositiva = filtrosClientes.condicion_impositiva;
          if (filtrosClientes.ingresos_brutos) params.ingresos_brutos = filtrosClientes.ingresos_brutos;
          if (filtrosClientes.drg) params.drg = filtrosClientes.drg;
          if (filtrosClientes.dv) params.dv = filtrosClientes.dv;
          if (filtrosClientes.iva_liquida !== '') params.iva_liquida = filtrosClientes.iva_liquida;
          response = await informesAPI.clientesSaldo(params);
          break;
        case 'movimientos':
          if (!fechaDesde || !fechaHasta) {
            toast.error('Seleccione fecha desde y fecha hasta');
            setLoading(false);
            return;
          }
          if (tipoCliente) params.tipo_cliente = tipoCliente;
          response = await informesAPI.movimientosPeriodo(params);
          break;
        case 'formas-pago':
          if (!fechaDesde || !fechaHasta) {
            toast.error('Seleccione fecha desde y fecha hasta');
            setLoading(false);
            return;
          }
          response = await informesAPI.formasPago(params);
          break;
        case 'evolucion':
          if (!fechaDesde || !fechaHasta) {
            toast.error('Seleccione fecha desde y fecha hasta');
            setLoading(false);
            return;
          }
          if (tipoCliente) params.tipo_cliente = tipoCliente;
          response = await informesAPI.evolucionSaldo(params);
          break;
        case 'movimientos-fondos':
          if (cuentasSeleccionadas.length) params.cuenta_bancaria_ids = cuentasSeleccionadas.join(',');
          if (cajasSeleccionadas.length) params.caja_ids = cajasSeleccionadas.join(',');
          response = await informesAPI.getMovimientosFondos(params);
          if (response.success) {
            setSaldoInicialFondos(response.saldo_inicial !== undefined ? response.saldo_inicial : null);
            setTituloEntidadFondos(response.titulo_entidad || null);
            setFiltroUnicoFondos(!!response.filtro_unico);
          }
          break;
        case 'saldos-fondos':
          response = await informesAPI.getSaldosFondos(params);
          break;
        case 'fondos-tipo-proveedor':
          if (cuentasSeleccionadas.length) params.cuenta_bancaria_ids = cuentasSeleccionadas.join(',');
          if (cajasSeleccionadas.length) params.caja_ids = cajasSeleccionadas.join(',');
          if (tipoProveedor) params.tipo_proveedor_id = tipoProveedor;
          response = await informesAPI.fondosPorTipoProveedor(params);
          if (response.success) {
            setDatos({ filas: response.data, resumen: response.resumen });
            setModalInformeAbierto(true);
            setLoading(false);
            return;
          }
          break;
        case 'abonos-cargados-cobrados':
          if (tipoCliente) params.tipo_id = tipoCliente;
          response = await informesAPI.abonosCargadosCobrados(params);
          if (response.success) {
            setDatos(response.data);
            setModalInformeAbierto(true);
            setLoading(false);
            return;
          }
          break;
        case 'cierre-fondos':
          if (!fechaCierre) {
            toast.error('Seleccione la fecha de cierre');
            setLoading(false);
            return;
          }
          if (!cajasSeleccionadas.length && !cuentasSeleccionadas.length) {
            toast.error('Seleccione al menos una caja o cuenta');
            setLoading(false);
            return;
          }
          response = await informesAPI.cierreFondos({
            fecha: fechaCierre,
            caja_ids: cajasSeleccionadas.join(','),
            cuenta_bancaria_ids: cuentasSeleccionadas.join(','),
          });
          if (response.success) {
            setDatos({
              fecha: response.fecha,
              entidades: response.entidades,
              resumen: response.resumen,
            });
            setModalInformeAbierto(true);
            setLoading(false);
            return;
          }
          break;
        default:
          setLoading(false);
          return;
      }

      if (response.success) {
        setDatos(response.data);
        setModalInformeAbierto(true);
        // Limpiar selección cuando se genera un nuevo informe
        if (tipoInforme === 'clientes-saldo') {
          setClientesSeleccionados([]);
        }
      }
    } catch (error) {
      toast.error('Error al generar informe');
    } finally {
      setLoading(false);
    }
  };

  const imprimirInforme = () => {
    const ventanaImpresion = window.open('', '_blank');
    const contenido = generarContenidoImpresion();
    ventanaImpresion.document.write(contenido);
    ventanaImpresion.document.close();
    ventanaImpresion.focus();
    setTimeout(() => {
      ventanaImpresion.print();
    }, 250);
  };

  const generarContenidoImpresion = () => {
    const titulo = getTituloInforme();
    const fechaGeneracion = formatFechaHora(new Date());
    
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${titulo}</title>
        <style>
          ${tipoInforme === 'movimientos-fondos' ? '@page { size: A4 landscape; margin: 6mm; }' : ''}
          body {
            font-family: Arial, sans-serif;
            max-width: 1200px;
            margin: 0 auto;
            padding: 20px;
            ${tipoInforme === 'movimientos-fondos' ? 'font-size: 8pt;' : ''}
          }
          ${tipoInforme === 'movimientos-fondos' ? `
          table { width: 100%; border-collapse: collapse; white-space: nowrap; font-size: 8pt; }
          th, td { border: 1px solid #ccc; padding: 3px 5px; }
          ` : ''}
          ${estilosPrintHeaderInforme}
          .fecha-generacion {
            text-align: right;
            color: #666;
            font-size: 12px;
            margin-bottom: 20px;
          }
          .stats-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 20px;
            margin-bottom: 30px;
          }
          .stat-item {
            background: #f5f5f5;
            padding: 15px;
            border-radius: 8px;
            text-align: center;
          }
          .stat-item label {
            display: block;
            font-weight: bold;
            margin-bottom: 8px;
            color: #555;
          }
          .stat-item span {
            font-size: 24px;
            font-weight: bold;
          }
          .positive { color: #27ae60; }
          .negative { color: #e74c3c; }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 30px;
          }
          th, td {
            padding: 10px;
            text-align: left;
            border-bottom: 1px solid #ddd;
          }
          th {
            background: #333;
            color: white;
          }
          .grafico-container {
            margin: 30px 0;
            page-break-inside: avoid;
          }
          @media print {
            body { margin: 0; padding: 15px; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        ${buildPrintHeaderHtml({
          titulo,
          logoUrl: getPrintLogoUrl(),
          encabezado: getPrintEncabezado(),
          fecha: fechaGeneracion,
        })}
        ${generarContenidoHTML()}
      </body>
      </html>
    `;
  };

  const getTituloInforme = () => {
    const titulos = {
      'general': 'Resumen General',
      'cliente': 'Resumen por Cliente',
      'tipo': 'Resumen por Tipo de Cliente',
      'clientes-saldo': 'Clientes con Saldo',
      'movimientos': 'Movimientos por Período',
      'formas-pago': 'Análisis de Formas de Pago',
      'evolucion': 'Evolución de Saldos',
      'movimientos-fondos': 'Movimientos de fondos (planilla)',
      'saldos-fondos': 'Saldos de fondos (cajas y cuentas)',
      'fondos-tipo-proveedor': 'Movimientos de fondos por tipo de proveedor',
      'abonos-cargados-cobrados': 'Abonos cargados vs cobrados',
      'cierre-fondos': 'Cierre de caja / cuentas',
    };
    return titulos[tipoInforme] || 'Informe';
  };

  const generarContenidoHTML = () => {
    if (!datos) return '';

    switch (tipoInforme) {
      case 'general':
        return `
          <div class="stats-grid">
            <div class="stat-item">
              <label>Total Clientes</label>
              <span>${datos.total_clientes || 0}</span>
            </div>
            <div class="stat-item">
              <label>Clientes Activos</label>
              <span>${datos.clientes_activos || 0}</span>
            </div>
            <div class="stat-item">
              <label>Total Movimientos</label>
              <span>${datos.total_movimientos || 0}</span>
            </div>
            <div class="stat-item">
              <label>Total Débitos</label>
              <span class="negative">$${parseFloat(datos.total_debitos || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
            </div>
            <div class="stat-item">
              <label>Total Créditos</label>
              <span class="positive">$${parseFloat(datos.total_creditos || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
            </div>
            <div class="stat-item">
              <label>Saldo Total</label>
              <span class="${parseFloat(datos.saldo_total || 0) >= 0 ? 'positive' : 'negative'}">
                $${parseFloat(datos.saldo_total || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
              </span>
            </div>
          </div>
        `;
      case 'cliente':
        return `
          <div style="margin-bottom: 20px;">
            <h3>Cliente: ${datos.cliente.razon_social}</h3>
            <p><strong>CUIT:</strong> ${datos.cliente.cuit}</p>
          </div>
          <div class="stats-grid">
            <div class="stat-item">
              <label>Total Movimientos</label>
              <span>${datos.resumen.total_movimientos || 0}</span>
            </div>
            <div class="stat-item">
              <label>Total Débitos</label>
              <span class="negative">$${parseFloat(datos.resumen.total_debitos || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
            </div>
            <div class="stat-item">
              <label>Total Créditos</label>
              <span class="positive">$${parseFloat(datos.resumen.total_creditos || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
            </div>
            <div class="stat-item">
              <label>Saldo</label>
              <span class="${parseFloat(datos.resumen.saldo || 0) >= 0 ? 'positive' : 'negative'}">
                $${parseFloat(datos.resumen.saldo || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
              </span>
            </div>
          </div>
        `;
      case 'tipo':
        if (!Array.isArray(datos)) return '';
        return `
          <table>
            <thead>
              <tr>
                <th>Tipo</th>
                <th>Cantidad Clientes</th>
                <th>Total Movimientos</th>
                <th>Total Débitos</th>
                <th>Total Créditos</th>
                <th>Saldo Total</th>
              </tr>
            </thead>
            <tbody>
              ${datos.map(item => `
                <tr>
                  <td>${item.tipo}</td>
                  <td>${item.cantidad_clientes || 0}</td>
                  <td>${item.total_movimientos || 0}</td>
                  <td class="negative">$${parseFloat(item.total_debitos || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</td>
                  <td class="positive">$${parseFloat(item.total_creditos || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</td>
                  <td class="${parseFloat(item.saldo_total || 0) >= 0 ? 'positive' : 'negative'}">
                    $${parseFloat(item.saldo_total || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                  </td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        `;
      case 'clientes-saldo':
        if (!Array.isArray(datos)) return '';
        return `
          <table>
            <thead>
              <tr>
                <th>CUIT</th>
                <th>Razón Social</th>
                <th>Tipo</th>
                <th>Cond. imp.</th>
                <th>IIBB</th>
                <th>DRG</th>
                <th>DV</th>
                <th>IVA líquida</th>
                <th>Total Débitos</th>
                <th>Total Créditos</th>
                <th>Saldo</th>
              </tr>
            </thead>
            <tbody>
              ${datos.map(cliente => `
                <tr>
                  <td>${cliente.cuit}</td>
                  <td>${cliente.razon_social}</td>
                  <td>${cliente.tipo}</td>
                  <td>${cliente.condicion_impositiva || '-'}</td>
                  <td>${cliente.ingresos_brutos || '-'}</td>
                  <td>${cliente.drg || '-'}</td>
                  <td>${cliente.dv || '-'}</td>
                  <td>${cliente.iva_liquida === 1 || cliente.iva_liquida === '1' ? 'Sí' : cliente.iva_liquida === 0 || cliente.iva_liquida === '0' ? 'No' : '-'}</td>
                  <td class="negative">$${parseFloat(cliente.total_debitos || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</td>
                  <td class="positive">$${parseFloat(cliente.total_creditos || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</td>
                  <td class="${parseFloat(cliente.saldo || 0) >= 0 ? 'positive' : 'negative'}">
                    $${parseFloat(cliente.saldo || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                  </td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        `;
      case 'movimientos':
        if (!datos.movimientos || !Array.isArray(datos.movimientos)) return '';
        return `
          ${datos.resumen ? `
            <div class="stats-grid">
              <div class="stat-item">
                <label>Total Movimientos</label>
                <span>${datos.resumen.total_movimientos || 0}</span>
              </div>
              <div class="stat-item">
                <label>Total Débitos</label>
                <span class="negative">$${parseFloat(datos.resumen.total_debitos || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
              </div>
              <div class="stat-item">
                <label>Total Créditos</label>
                <span class="positive">$${parseFloat(datos.resumen.total_creditos || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
              </div>
            </div>
          ` : ''}
          <table>
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Cliente</th>
                <th>Tipo</th>
                <th>Concepto</th>
                <th>Monto</th>
                <th>N° Recibo</th>
                <th>Usuario</th>
              </tr>
            </thead>
            <tbody>
              ${datos.movimientos.map(mov => `
                <tr>
                  <td>${formatFecha(mov.fecha)}</td>
                  <td>${mov.razon_social}</td>
                  <td>${mov.tipo_movimiento}</td>
                  <td>${mov.concepto} ${mov.movimiento_predefinido_descripcion ? `(${mov.movimiento_predefinido_descripcion})` : ''}</td>
                  <td class="${mov.tipo_movimiento === 'DEBITO' ? 'negative' : 'positive'}">
                    $${parseFloat(mov.monto).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                  </td>
                  <td>${mov.numero_recibo || '-'}</td>
                  <td>${mov.usuario_nombre || '-'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        `;
      case 'movimientos-fondos':
        if (!Array.isArray(datos)) return '';
        return `
          <table class="planilla-fondos">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Clase</th>
                ${filtroUnicoFondos ? '' : '<th>Origen</th><th>Destino</th>'}
                <th>Concepto</th>
                ${filtroUnicoFondos ? '' : '<th>Destinatario</th>'}
                <th>Débito</th>
                <th>Crédito</th>
                ${filtroUnicoFondos ? '<th>Saldo</th>' : ''}
              </tr>
            </thead>
            <tbody>
              ${datos.map((row) => `
                <tr>
                  <td>${formatFecha(row.fecha)}</td>
                  <td>${row.clase || '-'}</td>
                  ${filtroUnicoFondos ? '' : `<td>${row.origen || '-'}</td><td>${row.destino || '-'}</td>`}
                  <td>${row.concepto || ''}</td>
                  ${filtroUnicoFondos ? '' : `<td>${row.destinatario || '-'}</td>`}
                  <td class="negative">${parseFloat(row.debito || 0) > 0 ? '$' + parseFloat(row.debito).toLocaleString('es-AR', { minimumFractionDigits: 2 }) : '-'}</td>
                  <td class="positive">${parseFloat(row.credito || 0) > 0 ? '$' + parseFloat(row.credito).toLocaleString('es-AR', { minimumFractionDigits: 2 }) : '-'}</td>
                  ${filtroUnicoFondos ? `<td>$${parseFloat(row.saldo_corrido || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</td>` : ''}
                </tr>
              `).join('')}
            </tbody>
          </table>
        `;
      case 'saldos-fondos':
        if (!Array.isArray(datos)) return '';
        return `
          <table>
            <thead><tr><th>Tipo</th><th>Nombre</th><th>Moneda</th><th>Saldo</th><th>Estado</th></tr></thead>
            <tbody>
              ${datos.map((r) => `
                <tr>
                  <td>${r.tipo_entidad === 'caja' ? 'Caja' : 'Cuenta'}</td>
                  <td>${r.nombre}</td>
                  <td>${r.moneda || 'ARS'}</td>
                  <td>$${parseFloat(r.saldo || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</td>
                  <td>${r.activo ? 'Activo' : 'Inactivo'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        `;
      case 'fondos-tipo-proveedor': {
        const filas = datos?.filas || [];
        const resumen = datos?.resumen || {};
        return `
          ${resumen.total_movimientos != null ? `
            <div class="stats-grid">
              <div class="stat-item"><label>Total movimientos</label><span>${resumen.total_movimientos || 0}</span></div>
              <div class="stat-item"><label>Total egresos</label><span class="negative">$${parseFloat(resumen.total_egresos || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span></div>
              <div class="stat-item"><label>Total ingresos</label><span class="positive">$${parseFloat(resumen.total_ingresos || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span></div>
              <div class="stat-item"><label>Egresos con proveedor</label><span>$${parseFloat(resumen.egresos_con_proveedor || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span></div>
            </div>
          ` : ''}
          <table>
            <thead>
              <tr>
                <th>Tipo de proveedor</th>
                <th>Cant. proveedores</th>
                <th>Cant. movimientos</th>
                <th>Total egresos</th>
                <th>Total ingresos</th>
              </tr>
            </thead>
            <tbody>
              ${filas.map((item) => `
                <tr>
                  <td>${item.tipo_proveedor}</td>
                  <td>${item.cantidad_proveedores || 0}</td>
                  <td>${item.cantidad_movimientos || 0}</td>
                  <td class="negative">$${parseFloat(item.total_egresos || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</td>
                  <td class="positive">$${parseFloat(item.total_ingresos || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        `;
      }
      case 'abonos-cargados-cobrados': {
        const resumen = datos || {};
        const pendientes = Array.isArray(resumen.clientes_pendientes) ? resumen.clientes_pendientes : [];
        return `
          <div class="stats-grid">
            <div class="stat-item">
              <label>Abonos cargados</label>
              <span class="negative">$${parseFloat(resumen.cargados || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
            </div>
            <div class="stat-item">
              <label>Abonos cobrados</label>
              <span class="positive">$${parseFloat(resumen.cobrados || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
            </div>
            <div class="stat-item">
              <label>Diferencia</label>
              <span class="${parseFloat(resumen.diferencia || 0) >= 0 ? 'negative' : 'positive'}">$${parseFloat(resumen.diferencia || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
            </div>
            <div class="stat-item">
              <label>Clientes pendientes</label>
              <span>${resumen.pendientes || 0}</span>
            </div>
          </div>
          ${pendientes.length ? `
            <table>
              <thead><tr><th>Cliente</th><th>CUIT</th><th>Cargado</th><th>Cobrado</th><th>Pendiente</th></tr></thead>
              <tbody>
                ${pendientes.map((c) => `
                  <tr>
                    <td>${c.razon_social || ''}</td>
                    <td>${c.cuit || ''}</td>
                    <td>$${parseFloat(c.cargado || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</td>
                    <td>$${parseFloat(c.cobrado || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</td>
                    <td>$${parseFloat(c.pendiente || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          ` : '<p>Sin clientes con saldo pendiente en el período.</p>'}
        `;
      }
      case 'cierre-fondos': {
        const resumen = datos?.resumen || {};
        const entidades = datos?.entidades || [];
        const fecha = datos?.fecha || '';
        const detalleHtml = entidades.map((e) => {
          const movs = e.movimientos || [];
          const movRows = movs.map((m) => `
            <tr>
              <td>${formatFecha(m.fecha)}</td>
              <td>${m.clase || ''}</td>
              <td>${m.concepto || ''}</td>
              <td>${m.debito ? fmtMoneyInforme(m.debito) : '-'}</td>
              <td>${m.credito ? fmtMoneyInforme(m.credito) : '-'}</td>
              <td>${fmtMoneyInforme(m.saldo_corrido)}</td>
            </tr>
          `).join('');
          return `
            <h3>${e.tipo_entidad === 'caja' ? 'Caja' : 'Cuenta'}: ${e.nombre}</h3>
            <p>Saldo inicial del día: ${fmtMoneyInforme(e.saldo_inicial_dia)} · Ingresos: ${fmtMoneyInforme(e.total_ingresos)} · Egresos: ${fmtMoneyInforme(e.total_egresos)} · Saldo al cierre: <strong>${fmtMoneyInforme(e.saldo_cierre)}</strong></p>
            ${movs.length ? `
              <table>
                <thead><tr><th>Fecha</th><th>Clase</th><th>Concepto</th><th>Egreso</th><th>Ingreso</th><th>Saldo</th></tr></thead>
                <tbody>${movRows}</tbody>
              </table>
            ` : '<p>Sin movimientos en la fecha.</p>'}
          `;
        }).join('');
        return `
          <p><strong>Fecha de cierre:</strong> ${formatFecha(fecha)}</p>
          <div class="stats-grid">
            <div class="stat-item"><label>Entidades</label><span>${resumen.total_entidades || 0}</span></div>
            <div class="stat-item"><label>Saldo total al cierre</label><span>${fmtMoneyInforme(resumen.saldo_total_cierre)}</span></div>
            <div class="stat-item"><label>Ingresos del día</label><span class="positive">${fmtMoneyInforme(resumen.total_ingresos)}</span></div>
            <div class="stat-item"><label>Egresos del día</label><span class="negative">${fmtMoneyInforme(resumen.total_egresos)}</span></div>
          </div>
          ${detalleHtml}
        `;
      }
      default:
        return '';
    }
  };

  const renderGraficos = () => {
    if (!datos || !mostrarGraficos) {
      return null;
    }
    
    try {

    switch (tipoInforme) {
      case 'general':
        const datosGraficoGeneral = [
          { name: 'Débitos', value: parseFloat(datos.total_debitos || 0) },
          { name: 'Créditos', value: parseFloat(datos.total_creditos || 0) }
        ];
        if (datosGraficoGeneral.every(d => d.value === 0)) {
          return (
            <div className="graficos-container">
              <h3>Gráficos</h3>
              <p>No hay datos para mostrar gráficos</p>
            </div>
          );
        }
        return (
          <div className="graficos-container">
            <h3>Gráficos</h3>
            <div className="grafico-item">
              <h4>Distribución Débitos vs Créditos</h4>
              <div style={{ width: '100%', height: '300px', display: 'flex', justifyContent: 'center', alignItems: 'center', overflow: 'hidden' }}>
                <PieChart width={600} height={300}>
                  <Pie
                    data={datosGraficoGeneral}
                    cx={300}
                    cy={150}
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {datosGraficoGeneral && datosGraficoGeneral.length > 0 && datosGraficoGeneral.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => `$${parseFloat(value).toLocaleString('es-AR', { minimumFractionDigits: 2 })}`} />
                </PieChart>
              </div>
            </div>
          </div>
        );

      case 'tipo':
        if (!Array.isArray(datos)) return null;
        const datosGraficoTipo = datos.map(item => ({
          name: item.tipo,
          debitos: parseFloat(item.total_debitos || 0),
          creditos: parseFloat(item.total_creditos || 0),
          saldo: parseFloat(item.saldo_total || 0)
        }));
        return (
          <div className="graficos-container">
            <h3>Gráficos</h3>
            <div className="grafico-item">
              <h4>Débitos y Créditos por Tipo</h4>
              <div style={{ width: '100%', height: '300px' }}>
                <BarChart width={600} height={300} data={datosGraficoTipo}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip formatter={(value) => `$${parseFloat(value).toLocaleString('es-AR', { minimumFractionDigits: 2 })}`} />
                  <Legend />
                  <Bar dataKey="debitos" fill="#e74c3c" name="Débitos" />
                  <Bar dataKey="creditos" fill="#27ae60" name="Créditos" />
                </BarChart>
              </div>
            </div>
            <div className="grafico-item">
              <h4>Saldo por Tipo</h4>
              <div style={{ width: '100%', height: '300px' }}>
                <BarChart width={600} height={300} data={datosGraficoTipo}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip formatter={(value) => `$${parseFloat(value).toLocaleString('es-AR', { minimumFractionDigits: 2 })}`} />
                  <Bar dataKey="saldo" fill="#667eea" name="Saldo" />
                </BarChart>
              </div>
            </div>
          </div>
        );

      case 'clientes-saldo':
        if (!Array.isArray(datos) || datos.length === 0) return null;
        const topClientes = [...datos]
          .sort((a, b) => Math.abs(parseFloat(b.saldo || 0)) - Math.abs(parseFloat(a.saldo || 0)))
          .slice(0, 10)
          .map(c => ({
            name: c.razon_social.length > 20 ? c.razon_social.substring(0, 20) + '...' : c.razon_social,
            saldo: parseFloat(c.saldo || 0)
          }));
        return (
          <div className="graficos-container">
            <h3>Gráficos</h3>
            <div className="grafico-item">
              <h4>Top 10 Clientes por Saldo</h4>
              <div style={{ width: '100%', height: '400px' }}>
                <BarChart width={800} height={400} data={topClientes} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" width={150} />
                  <Tooltip formatter={(value) => `$${parseFloat(value).toLocaleString('es-AR', { minimumFractionDigits: 2 })}`} />
                  <Bar dataKey="saldo" fill="#667eea" name="Saldo" />
                </BarChart>
              </div>
            </div>
          </div>
        );

      case 'movimientos':
        if (!datos.movimientos || !Array.isArray(datos.movimientos) || datos.movimientos.length === 0) return null;
        
        // Agrupar movimientos por fecha
        const movimientosPorFecha = {};
        datos.movimientos.forEach(mov => {
          const fecha = formatFecha(mov.fecha);
          if (!movimientosPorFecha[fecha]) {
            movimientosPorFecha[fecha] = { fecha, debitos: 0, creditos: 0 };
          }
          if (mov.tipo_movimiento === 'DEBITO') {
            movimientosPorFecha[fecha].debitos += parseFloat(mov.monto);
          } else {
            movimientosPorFecha[fecha].creditos += parseFloat(mov.monto);
          }
        });
        
        const datosGraficoMovimientos = Object.values(movimientosPorFecha).sort((a, b) => 
          new Date(a.fecha.split('/').reverse().join('-')) - new Date(b.fecha.split('/').reverse().join('-'))
        );
        
        return (
          <div className="graficos-container">
            <h3>Gráficos</h3>
            <div className="grafico-item">
              <h4>Evolución Diaria de Movimientos</h4>
              <div style={{ width: '100%', height: '300px' }}>
                <LineChart width={800} height={300} data={datosGraficoMovimientos}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="fecha" />
                  <YAxis />
                  <Tooltip formatter={(value) => `$${parseFloat(value).toLocaleString('es-AR', { minimumFractionDigits: 2 })}`} />
                  <Legend />
                  <Line type="monotone" dataKey="debitos" stroke="#e74c3c" name="Débitos" />
                  <Line type="monotone" dataKey="creditos" stroke="#27ae60" name="Créditos" />
                </LineChart>
              </div>
            </div>
          </div>
        );

      case 'formas-pago':
        if (!Array.isArray(datos) || datos.length === 0) return null;
        const datosGraficoFormasPago = datos.map(item => ({
          name: item.forma_pago,
          cantidad: parseInt(item.cantidad || 0),
          monto: parseFloat(item.monto_total || 0)
        }));
        return (
          <div className="graficos-container">
            <h3>Gráficos</h3>
            <div className="grafico-item">
              <h4>Distribución por Forma de Pago (Cantidad)</h4>
              <div style={{ width: '100%', height: '300px', display: 'flex', justifyContent: 'center', alignItems: 'center', overflow: 'hidden' }}>
                <PieChart width={600} height={300}>
                  <Pie
                    data={datosGraficoFormasPago}
                    cx={300}
                    cy={150}
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="cantidad"
                  >
                    {datosGraficoFormasPago && datosGraficoFormasPago.length > 0 && datosGraficoFormasPago.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </div>
            </div>
            <div className="grafico-item">
              <h4>Monto Total por Forma de Pago</h4>
              <div style={{ width: '100%', height: '300px' }}>
                <BarChart width={600} height={300} data={datosGraficoFormasPago}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip formatter={(value) => `$${parseFloat(value).toLocaleString('es-AR', { minimumFractionDigits: 2 })}`} />
                  <Bar dataKey="monto" fill="#667eea" name="Monto Total" />
                </BarChart>
              </div>
            </div>
          </div>
        );

      case 'evolucion':
        if (!Array.isArray(datos) || datos.length === 0) return null;
        return (
          <div className="graficos-container">
            <h3>Gráficos</h3>
            <div className="grafico-item">
              <h4>Evolución de Saldos</h4>
              <div style={{ width: '100%', height: '400px' }}>
                <LineChart width={800} height={400} data={datos}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="fecha" />
                  <YAxis />
                  <Tooltip formatter={(value) => `$${parseFloat(value).toLocaleString('es-AR', { minimumFractionDigits: 2 })}`} />
                  <Legend />
                  <Line type="monotone" dataKey="saldo_promedio" stroke="#667eea" name="Saldo Promedio" />
                  <Line type="monotone" dataKey="saldo_total" stroke="#e74c3c" name="Saldo Total" />
                </LineChart>
              </div>
            </div>
          </div>
        );

      case 'fondos-tipo-proveedor': {
        const filas = datos?.filas || [];
        if (!filas.length) return null;
        const datosGraficoFondosProv = filas.map((item) => ({
          name: (item.tipo_proveedor || '').length > 22
            ? `${item.tipo_proveedor.substring(0, 22)}…`
            : item.tipo_proveedor,
          egresos: parseFloat(item.total_egresos || 0),
          ingresos: parseFloat(item.total_ingresos || 0),
        }));
        return (
          <div className="graficos-container">
            <h3>Gráficos</h3>
            <div className="grafico-item">
              <h4>Egresos e ingresos por tipo de proveedor</h4>
              <div style={{ width: '100%', height: '360px' }}>
                <BarChart width={800} height={360} data={datosGraficoFondosProv}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip formatter={(value) => `$${parseFloat(value).toLocaleString('es-AR', { minimumFractionDigits: 2 })}`} />
                  <Legend />
                  <Bar dataKey="egresos" fill="#e74c3c" name="Egresos" />
                  <Bar dataKey="ingresos" fill="#27ae60" name="Ingresos" />
                </BarChart>
              </div>
            </div>
          </div>
        );
      }

      default:
        return null;
      }
    } catch (error) {
      console.error('Error al renderizar gráficos:', error);
      return (
        <div className="graficos-container">
          <h3>Gráficos</h3>
          <p>Error al cargar gráficos. Por favor, intente nuevamente.</p>
        </div>
      );
    }
  };

  const exportarMovimientosFondos = (format) => {
    if (tipoInforme !== 'movimientos-fondos' || !Array.isArray(datos)) return;
    const headers = filtroUnicoFondos
      ? ['Fecha', 'Clase', 'Concepto', 'Débito', 'Crédito', 'Saldo']
      : ['Fecha', 'Clase', 'Origen', 'Destino', 'Concepto', 'Destinatario', 'Débito', 'Crédito'];
    const rows = datos.map((r) => (
      filtroUnicoFondos
        ? [formatFecha(r.fecha), r.clase || '', r.concepto || '', r.debito || 0, r.credito || 0, r.saldo_corrido || '']
        : [formatFecha(r.fecha), r.clase || '', r.origen || '', r.destino || '', r.concepto || '', r.destinatario || '', r.debito || 0, r.credito || 0]
    ));
    return exportListado({
      filename: 'planilla_movimientos_fondos',
      sheetName: 'Movimientos',
      headers,
      rows,
      format,
    });
  };

  const exportarClientesSaldo = (format) => {
    if (tipoInforme !== 'clientes-saldo' || !Array.isArray(datos)) return;
    const headers = ['CUIT', 'Razon Social', 'Tipo', 'Condicion Impositiva', 'Ingresos Brutos', 'ARCA', 'DRG', 'DV', 'IVA Liquida', 'Total Debitos', 'Total Creditos', 'Saldo'];
    const rows = datos.map((c) => ([
      c.cuit || '',
      c.razon_social || '',
      c.tipo || '',
      c.condicion_impositiva || '',
      c.ingresos_brutos || '',
      c.drg || '',
      c.dv || '',
      c.iva_liquida === 1 || c.iva_liquida === '1' ? 'SI' : c.iva_liquida === 0 || c.iva_liquida === '0' ? 'NO' : '',
      parseFloat(c.total_debitos || 0).toFixed(2),
      parseFloat(c.total_creditos || 0).toFixed(2),
      parseFloat(c.saldo || 0).toFixed(2),
    ]));
    return exportListado({
      filename: 'informe_clientes_saldo',
      sheetName: 'Clientes',
      headers,
      rows,
      format,
    });
  };

  const exportarCierreFondos = async (format) => {
    const entidades = datos?.entidades || [];
    if (!entidades.length) {
      toast.info('No hay datos para exportar');
      return;
    }
    const resumenHeaders = ['Tipo', 'Entidad', 'Moneda', 'Saldo inicial día', 'Ingresos día', 'Egresos día', 'Saldo cierre', 'Movimientos'];
    const resumenRows = entidades.map((e) => [
      e.tipo_entidad === 'caja' ? 'Caja' : 'Cuenta',
      e.nombre || '',
      e.moneda || 'ARS',
      (parseFloat(e.saldo_inicial_dia) || 0).toFixed(2),
      (parseFloat(e.total_ingresos) || 0).toFixed(2),
      (parseFloat(e.total_egresos) || 0).toFixed(2),
      (parseFloat(e.saldo_cierre) || 0).toFixed(2),
      e.cantidad_movimientos || 0,
    ]);
    const detalleHeaders = ['Entidad', 'Fecha', 'Clase', 'Concepto', 'Egreso', 'Ingreso', 'Saldo'];
    const detalleRows = [];
    entidades.forEach((e) => {
      (e.movimientos || []).forEach((m) => {
        detalleRows.push([
          e.nombre || '',
          formatFecha(m.fecha),
          m.clase || '',
          m.concepto || '',
          m.debito ? (parseFloat(m.debito).toFixed(2)) : '',
          m.credito ? (parseFloat(m.credito).toFixed(2)) : '',
          (parseFloat(m.saldo_corrido) || 0).toFixed(2),
        ]);
      });
    });
    if (format === 'csv') {
      const lines = [
        `Cierre de fondos — ${datos?.fecha || ''}`,
        '',
        resumenHeaders.join(';'),
        ...resumenRows.map((r) => r.join(';')),
        '',
        detalleHeaders.join(';'),
        ...detalleRows.map((r) => r.join(';')),
      ];
      const blob = new Blob(['\ufeff' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cierre_fondos_${datos?.fecha || 'informe'}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      return;
    }
    await exportListado({
      filename: `cierre_fondos_${datos?.fecha || 'informe'}`,
      sheetName: 'Resumen',
      headers: resumenHeaders,
      rows: resumenRows,
      format,
    });
  };

  const exportarInforme = async (format) => {
    try {
      if (tipoInforme === 'movimientos-fondos') {
        await exportarMovimientosFondos(format);
      } else if (tipoInforme === 'clientes-saldo') {
        await exportarClientesSaldo(format);
      } else if (tipoInforme === 'cierre-fondos') {
        await exportarCierreFondos(format);
      }
    } catch (e) {
      toast.error(e.message || 'Error al exportar');
    }
  };

  const paramsFondosTipoProveedor = () => {
    const params = {};
    if (fechaDesde) params.fecha_desde = fechaDesde;
    if (fechaHasta) params.fecha_hasta = fechaHasta;
    if (cuentasSeleccionadas.length) params.cuenta_bancaria_ids = cuentasSeleccionadas.join(',');
    if (cajasSeleccionadas.length) params.caja_ids = cajasSeleccionadas.join(',');
    return params;
  };

  const abrirDrillDownTipoProveedor = async (item) => {
    const tipoId = item.tipo_proveedor_id ?? 0;
    setDrillDownTipo({ id: tipoId, nombre: item.tipo_proveedor || 'Sin proveedor' });
    setModalDrillDownAbierto(true);
    setDrillDownMovimientos([]);
    setDrillDownLoading(true);
    try {
      const params = {
        ...paramsFondosTipoProveedor(),
        tipo_proveedor_id: tipoId,
      };
      const res = await informesAPI.fondosPorTipoProveedorMovimientos(params);
      if (res.success) {
        setDrillDownMovimientos(Array.isArray(res.data) ? res.data : []);
      } else {
        toast.error(res.message || 'No se pudieron cargar los movimientos');
      }
    } catch (e) {
      toast.error(e.message || 'Error al cargar movimientos');
    } finally {
      setDrillDownLoading(false);
    }
  };

  const cerrarDrillDown = () => {
    setModalDrillDownAbierto(false);
    setDrillDownTipo(null);
    setDrillDownMovimientos([]);
  };

  const exportarDrillDown = async (format) => {
    if (!drillDownMovimientos.length) {
      toast.info('No hay movimientos para exportar');
      return;
    }
    const headers = ['Fecha', 'Clase', 'Concepto', 'Proveedor', 'Origen', 'Destino', 'Egreso', 'Ingreso'];
    const rows = drillDownMovimientos.map((r) => [
      formatFecha(r.fecha),
      r.clase || '',
      r.concepto || '',
      r.proveedor || '',
      r.origen || '',
      r.destino || '',
      r.egreso ? parseFloat(r.egreso).toFixed(2) : '',
      r.ingreso ? parseFloat(r.ingreso).toFixed(2) : '',
    ]);
    try {
      await exportListado({
        filename: `fondos_tipo_${drillDownTipo?.nombre || 'proveedor'}`,
        sheetName: 'Movimientos',
        headers,
        rows,
        format,
      });
    } catch (e) {
      toast.error(e.message || 'Error al exportar');
    }
  };

  const getTituloModalInforme = () => {
    if (tipoInforme === 'movimientos-fondos' && tituloEntidadFondos) return tituloEntidadFondos;
    return getTituloInforme();
  };

  const cerrarModalInforme = () => {
    setModalInformeAbierto(false);
  };

  const exportarCsvInforme = () => exportarInforme('csv');
  const exportarExcelInforme = () => exportarInforme('excel');

  const informeTieneCsv = tipoInforme === 'clientes-saldo' || tipoInforme === 'movimientos-fondos' || tipoInforme === 'cierre-fondos';
  const informeTieneGraficos = ['general', 'tipo', 'clientes-saldo', 'movimientos', 'formas-pago', 'evolucion', 'fondos-tipo-proveedor'].includes(tipoInforme);

  const renderAccionesModalInforme = () => (
    <div className="informe-modal-actions">
      {informeTieneGraficos && (
        <button type="button" onClick={() => setMostrarGraficos(!mostrarGraficos)} className="btn-secondary">
          {mostrarGraficos ? 'Ocultar gráficos' : 'Mostrar gráficos'}
        </button>
      )}
      {informeTieneCsv && (
        <ExportActions
          onCsv={exportarCsvInforme}
          onExcel={exportarExcelInforme}
          showPrint={false}
        />
      )}
      <button type="button" onClick={imprimirInforme} className="btn-primary">🖨️ Imprimir</button>
      <button type="button" onClick={cerrarModalInforme} className="btn-secondary">Cerrar</button>
    </div>
  );

  const renderInforme = () => {
    if (!datos) return null;

    switch (tipoInforme) {
      case 'general':
        return (
          <div className="informe-content">
            <div className="stats-grid">
              <div className="stat-item">
                <label>Total Clientes</label>
                <span>{datos.total_clientes || 0}</span>
              </div>
              <div className="stat-item">
                <label>Clientes Activos</label>
                <span>{datos.clientes_activos || 0}</span>
              </div>
              <div className="stat-item">
                <label>Total Movimientos</label>
                <span>{datos.total_movimientos || 0}</span>
              </div>
              <div className="stat-item">
                <label>Total Débitos</label>
                <span className="negative">${parseFloat(datos.total_debitos || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="stat-item">
                <label>Total Créditos</label>
                <span className="positive">${parseFloat(datos.total_creditos || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="stat-item">
                <label>Saldo Total</label>
                <span className={parseFloat(datos.saldo_total || 0) >= 0 ? 'positive' : 'negative'}>
                  ${parseFloat(datos.saldo_total || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                </span>
              </div>
            </div>
            {renderGraficos()}
          </div>
        );

      case 'cliente':
        return (
          <div className="informe-content">
            <div className="cliente-info">
              <p><strong>Cliente:</strong> {datos.cliente.razon_social}</p>
              <p><strong>CUIT:</strong> {datos.cliente.cuit}</p>
              {datos.cliente.direccion && <p><strong>Dirección:</strong> {datos.cliente.direccion}</p>}
              {datos.cliente.telefono && <p><strong>Teléfono:</strong> {datos.cliente.telefono}</p>}
            </div>
            <div className="stats-grid">
              <div className="stat-item">
                <label>Total Movimientos</label>
                <span>{datos.resumen.total_movimientos || 0}</span>
              </div>
              <div className="stat-item">
                <label>Total Débitos</label>
                <span className="negative">${parseFloat(datos.resumen.total_debitos || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="stat-item">
                <label>Total Créditos</label>
                <span className="positive">${parseFloat(datos.resumen.total_creditos || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="stat-item">
                <label>Saldo</label>
                <span className={parseFloat(datos.resumen.saldo || 0) >= 0 ? 'positive' : 'negative'}>
                  ${parseFloat(datos.resumen.saldo || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          </div>
        );

      case 'tipo':
        return (
          <div className="informe-content">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Tipo</th>
                  <th>Cantidad Clientes</th>
                  <th>Total Movimientos</th>
                  <th>Total Débitos</th>
                  <th>Total Créditos</th>
                  <th>Saldo Total</th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(datos) && datos.map((item, idx) => (
                  <tr key={idx}>
                    <td><strong>{item.tipo}</strong></td>
                    <td>{item.cantidad_clientes || 0}</td>
                    <td>{item.total_movimientos || 0}</td>
                    <td className="negative">${parseFloat(item.total_debitos || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</td>
                    <td className="positive">${parseFloat(item.total_creditos || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</td>
                    <td className={parseFloat(item.saldo_total || 0) >= 0 ? 'positive' : 'negative'}>
                      ${parseFloat(item.saldo_total || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {renderGraficos()}
          </div>
        );

      case 'clientes-saldo':
        const handleSeleccionarCliente = (clienteId) => {
          setClientesSeleccionados(prev => {
            if (prev.includes(clienteId)) {
              return prev.filter(id => id !== clienteId);
            } else {
              return [...prev, clienteId];
            }
          });
        };

        const handleSeleccionarTodos = () => {
          if (Array.isArray(datos) && datos.length > 0) {
            if (clientesSeleccionados.length === datos.length) {
              setClientesSeleccionados([]);
            } else {
              setClientesSeleccionados(datos.map(c => c.id));
            }
          }
        };

        const generarMensajeWhatsApp = (cliente) => {
          const saldo = parseFloat(cliente.saldo || 0);
          const tipoSaldo = saldo > 0 ? 'DEUDOR' : saldo < 0 ? 'ACREEDOR' : 'SALDO CERO';
          const signoSaldo = saldo > 0 ? '+' : '';
          
          let mensaje = `*ESTADO DE CUENTA*\n\n`;
          mensaje += `*Cliente:* ${cliente.razon_social}\n`;
          mensaje += `*CUIT:* ${cliente.cuit}\n`;
          if (cliente.email) {
            mensaje += `*Email:* ${cliente.email}\n`;
          }
          mensaje += `\n*Resumen de Movimientos:*\n`;
          mensaje += `Total Débitos: $${parseFloat(cliente.total_debitos || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}\n`;
          mensaje += `Total Créditos: $${parseFloat(cliente.total_creditos || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}\n`;
          mensaje += `\n*SALDO ACTUAL: ${tipoSaldo}*\n`;
          mensaje += `$${signoSaldo}${Math.abs(saldo).toLocaleString('es-AR', { minimumFractionDigits: 2 })}\n`;
          mensaje += `\nFecha del informe: ${formatFecha(fechaHoy())}`;

          return mensaje;
        };

        const enviarWhatsApp = async (cliente) => {
          const tel = cliente.celular || cliente.telefono;
          if (!tel) {
            toast.error(`El cliente ${cliente.razon_social} no tiene celular ni teléfono registrado`);
            return;
          }
          try {
            await assertHorarioUsoCliente();
          } catch (e) {
            toast.error(e.message);
            return;
          }
          const numeroCompleto = formatearTelefonoInforme(tel).replace('+', '');
          const texto = generarMensajeWhatsApp(cliente);
          if (waUseBaileys) {
            if (!waStatus?.reachable) {
              toast.error('No se alcanza el servicio WhatsApp.');
              return;
            }
            if (!waStatus?.connected) {
              toast.warning('WhatsApp no está conectado. Escanee el QR en el servidor.');
              return;
            }
            try {
              await sendWhatsAppSequential(
                [{ phone: numeroCompleto, caption: texto, clienteId: cliente.id, destinatario: cliente.razon_social }],
                { sendRateProfile: waSendRateProfile }
              );
              toast.success('Mensaje enviado.');
            } catch (e) {
              toast.error(e.message || 'Error al enviar');
            }
            return;
          }
          abrirWhatsappWeb(numeroCompleto, texto);
        };

        const enviarWhatsAppMultiples = async () => {
          if (clientesSeleccionados.length === 0) {
            toast.error('Por favor, seleccione al menos un cliente');
            return;
          }

          const clientesSeleccionadosData = datos.filter((c) => clientesSeleccionados.includes(c.id));
          const conTel = clientesSeleccionadosData.filter((c) => c.celular || c.telefono);
          if (conTel.length === 0) {
            toast.error('Ningún cliente seleccionado tiene celular o teléfono');
            return;
          }

          try {
            await assertHorarioUsoCliente();
          } catch (e) {
            toast.error(e.message);
            return;
          }

          if (waUseBaileys) {
            if (!waStatus?.reachable) {
              toast.error('No se alcanza el servicio WhatsApp.');
              return;
            }
            if (!waStatus?.connected) {
              toast.warning('WhatsApp no está conectado. Escanee el QR en el servidor.');
              return;
            }
            try {
              const items = conTel.map((c) => ({
                phone: formatearTelefonoInforme(c.celular || c.telefono).replace('+', ''),
                caption: generarMensajeWhatsApp(c),
                clienteId: c.id,
                destinatario: c.razon_social,
              }));
              const { ok, fail, abortedEarly, abortMessage } = await sendWhatsAppSequential(items, {
                sendRateProfile: waSendRateProfile,
              });
              if (abortedEarly && abortMessage) {
                toast.warning(`${abortMessage} Enviados: ${ok}.`);
              } else {
                toast.success(`Enviados ${ok} mensaje(s)${fail ? `; ${fail} con error` : ''}.`);
              }
            } catch (e) {
              toast.error(e.message || 'Error al enviar');
            }
            return;
          }

          abrirWhatsappWebEscalonado(
            conTel.map((c) => ({
              numero: formatearTelefonoInforme(c.celular || c.telefono).replace('+', ''),
              texto: generarMensajeWhatsApp(c),
            })),
            1000
          );
          toast.success(`Se abrirán ${conTel.length} conversaciones de WhatsApp`);
        };

        return (
          <div className="informe-content">
            <WhatsAppServicioPanel
              useBaileys={waUseBaileys}
              onToggleUseBaileys={handleToggleWaBaileys}
              status={waStatus}
              loading={waStatusLoading}
              onRefresh={refreshWaStatus}
            />
            {clientesSeleccionados.length > 0 && (
              <div style={{ 
                padding: '10px', 
                marginBottom: '15px', 
                backgroundColor: '#e8f5e9', 
                borderRadius: '4px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '10px'
              }}>
                <span>
                  <strong>{clientesSeleccionados.length}</strong> cliente(s) seleccionado(s)
                </span>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <button 
                    type="button"
                    onClick={() => void enviarWhatsAppMultiples()} 
                    className="btn-primary"
                    style={{ backgroundColor: '#25D366', borderColor: '#25D366' }}
                  >
                    📱 Enviar WhatsApp ({clientesSeleccionados.length})
                  </button>
                  <button 
                    onClick={() => setClientesSeleccionados([])} 
                    className="btn-secondary"
                    style={{ padding: '5px 10px', fontSize: '12px' }}
                  >
                    Deseleccionar todos
                  </button>
                </div>
              </div>
            )}
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}>
                      <input
                        type="checkbox"
                        checked={Array.isArray(datos) && datos.length > 0 && clientesSeleccionados.length === datos.length}
                        onChange={handleSeleccionarTodos}
                        title="Seleccionar todos"
                      />
                    </th>
                    <th>CUIT</th>
                    <th>Razón Social</th>
                    <th>Tipo</th>
                    <th>Total Débitos</th>
                    <th>Total Créditos</th>
                    <th>Saldo</th>
                    <th>Cond. imp.</th>
                    <th>IIBB</th>
                    <th>DRG</th>
                    <th>DV</th>
                    <th>IVA líquida</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.isArray(datos) && datos.map(cliente => (
                    <tr key={cliente.id} style={{ backgroundColor: clientesSeleccionados.includes(cliente.id) ? '#e3f2fd' : 'transparent' }}>
                      <td>
                        <input
                          type="checkbox"
                          checked={clientesSeleccionados.includes(cliente.id)}
                          onChange={() => handleSeleccionarCliente(cliente.id)}
                        />
                      </td>
                      <td>{cliente.cuit}</td>
                      <td>
                        <div style={{ fontWeight: '500' }}>{cliente.razon_social}</div>
                        {cliente.email && (
                          <div style={{ fontSize: '12px', color: '#7f8c8d' }}>{cliente.email}</div>
                        )}
                      </td>
                      <td>{cliente.tipo}</td>
                      <td className="negative">${parseFloat(cliente.total_debitos || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</td>
                      <td className="positive">${parseFloat(cliente.total_creditos || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</td>
                      <td className={parseFloat(cliente.saldo || 0) > 0 ? 'saldo-deudor' : parseFloat(cliente.saldo || 0) < 0 ? 'saldo-acreedor' : ''}>
                        <strong>${parseFloat(cliente.saldo || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</strong>
                      </td>
                      <td>{cliente.condicion_impositiva || '-'}</td>
                      <td>{cliente.ingresos_brutos || '-'}</td>
                      <td>{cliente.drg || '-'}</td>
                      <td>{cliente.dv || '-'}</td>
                      <td>{cliente.iva_liquida === 1 || cliente.iva_liquida === '1' ? 'Sí' : cliente.iva_liquida === 0 || cliente.iva_liquida === '0' ? 'No' : '-'}</td>
                      <td>
                        {cliente.celular || cliente.telefono ? (
                          <button
                            type="button"
                            onClick={() => void enviarWhatsApp(cliente)}
                            className="btn-primary"
                            style={{ 
                              padding: '6px 12px', 
                              fontSize: '12px',
                              backgroundColor: '#25D366',
                              borderColor: '#25D366'
                            }}
                            title={`Enviar WhatsApp a ${cliente.razon_social}`}
                          >
                            📱
                          </button>
                        ) : (
                          <span style={{ color: '#999', fontSize: '12px' }}>Sin teléfono</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {renderGraficos()}
          </div>
        );

      case 'movimientos':
        return (
          <div className="informe-content">
            {datos.resumen && (
              <div className="resumen-box">
                <div className="stats-grid">
                  <div className="stat-item">
                    <label>Total Movimientos</label>
                    <span>{datos.resumen.total_movimientos || 0}</span>
                  </div>
                  <div className="stat-item">
                    <label>Total Débitos</label>
                    <span className="negative">${parseFloat(datos.resumen.total_debitos || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="stat-item">
                    <label>Total Créditos</label>
                    <span className="positive">${parseFloat(datos.resumen.total_creditos || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
                  </div>
                </div>
              </div>
            )}
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Cliente</th>
                    <th>Tipo</th>
                    <th>Concepto</th>
                    <th>Monto</th>
                    <th>N° Recibo</th>
                    <th>Usuario</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.isArray(datos.movimientos) && datos.movimientos.map(mov => (
                    <tr key={mov.id}>
                      <td>{formatFecha(mov.fecha)}</td>
                      <td>
                        <div style={{ fontWeight: '500' }}>{mov.razon_social}</div>
                        <div style={{ fontSize: '12px', color: '#7f8c8d' }}>{mov.cuit}</div>
                      </td>
                      <td>
                        <span className={`badge ${mov.tipo_movimiento === 'DEBITO' ? 'debito' : 'credito'}`}>
                          {mov.tipo_movimiento}
                        </span>
                      </td>
                      <td>
                        <div>{mov.concepto}</div>
                        {mov.movimiento_predefinido_descripcion && (
                          <div style={{ fontSize: '11px', color: '#667eea', marginTop: '2px', fontStyle: 'italic' }}>
                            📋 {mov.movimiento_predefinido_descripcion}
                          </div>
                        )}
                      </td>
                      <td className={mov.tipo_movimiento === 'DEBITO' ? 'negative' : 'positive'}>
                        <strong>${parseFloat(mov.monto).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</strong>
                      </td>
                      <td>{mov.numero_recibo || '-'}</td>
                      <td>{mov.usuario_nombre || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {renderGraficos()}
          </div>
        );

      case 'formas-pago':
        return (
          <div className="informe-content">
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Forma de Pago</th>
                    <th>Cantidad</th>
                    <th>Monto Total</th>
                    <th>Porcentaje</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.isArray(datos) && datos.map((item, idx) => {
                    const total = datos.reduce((sum, i) => sum + parseFloat(i.monto_total || 0), 0);
                    const porcentaje = total > 0 ? (parseFloat(item.monto_total || 0) / total * 100).toFixed(2) : 0;
                    return (
                      <tr key={idx}>
                        <td><strong>{item.forma_pago}</strong></td>
                        <td>{item.cantidad || 0}</td>
                        <td className="positive">${parseFloat(item.monto_total || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</td>
                        <td>{porcentaje}%</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {renderGraficos()}
          </div>
        );

      case 'movimientos-fondos':
        return (
          <div className="informe-content">
            {saldoInicialFondos !== null && filtroUnicoFondos && (
              <div className="resumen-box" style={{ marginBottom: 16 }}>
                <strong>Saldo inicial del período:</strong>{' '}
                ${parseFloat(saldoInicialFondos).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
              </div>
            )}
            <div className="table-container informe-fondos-tabla">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Clase</th>
                    {!filtroUnicoFondos && <th>Origen</th>}
                    {!filtroUnicoFondos && <th>Destino</th>}
                    <th>Concepto</th>
                    {!filtroUnicoFondos && <th>Destinatario</th>}
                    <th>Débito</th>
                    <th>Crédito</th>
                    {filtroUnicoFondos && <th>Saldo</th>}
                  </tr>
                </thead>
                <tbody>
                  {Array.isArray(datos) && datos.map((row, idx) => (
                    <tr key={idx}>
                      <td>{formatFecha(row.fecha)}</td>
                      <td>{row.clase || '-'}</td>
                      {!filtroUnicoFondos && <td title={row.origen}>{row.origen || '-'}</td>}
                      {!filtroUnicoFondos && <td title={row.destino}>{row.destino || '-'}</td>}
                      <td title={row.concepto} style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.concepto}</td>
                      {!filtroUnicoFondos && <td>{row.destinatario || '-'}</td>}
                      <td className="negative">
                        {parseFloat(row.debito || 0) > 0
                          ? `$${parseFloat(row.debito).toLocaleString('es-AR', { minimumFractionDigits: 2 })}`
                          : '-'}
                      </td>
                      <td className="positive">
                        {parseFloat(row.credito || 0) > 0
                          ? `$${parseFloat(row.credito).toLocaleString('es-AR', { minimumFractionDigits: 2 })}`
                          : '-'}
                      </td>
                      {filtroUnicoFondos && (
                        <td className={parseFloat(row.saldo_corrido || 0) >= 0 ? 'positive' : 'negative'}>
                          ${parseFloat(row.saldo_corrido || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 'saldos-fondos':
        return (
          <div className="informe-content">
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr><th>Tipo</th><th>Nombre</th><th>Moneda</th><th>Saldo</th><th>Estado</th></tr>
                </thead>
                <tbody>
                  {Array.isArray(datos) && datos.map((r, idx) => (
                    <tr key={idx}>
                      <td>{r.tipo_entidad === 'caja' ? 'Caja' : 'Cuenta'}</td>
                      <td>{r.nombre}</td>
                      <td>{r.moneda || 'ARS'}</td>
                      <td><strong>${parseFloat(r.saldo || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</strong></td>
                      <td>{r.activo ? 'Activo' : 'Inactivo'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 'fondos-tipo-proveedor': {
        const filas = datos?.filas || [];
        const resumen = datos?.resumen || {};
        return (
          <div className="informe-content">
            <div className="stats-grid">
              <div className="stat-item">
                <label>Total movimientos</label>
                <span>{resumen.total_movimientos || 0}</span>
              </div>
              <div className="stat-item">
                <label>Total egresos</label>
                <span className="negative">${parseFloat(resumen.total_egresos || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="stat-item">
                <label>Total ingresos</label>
                <span className="positive">${parseFloat(resumen.total_ingresos || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="stat-item">
                <label>Egresos con proveedor</label>
                <span>${parseFloat(resumen.egresos_con_proveedor || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
              </div>
            </div>
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Tipo de proveedor</th>
                    <th>Cant. proveedores</th>
                    <th>Cant. movimientos</th>
                    <th>Total egresos</th>
                    <th>Total ingresos</th>
                  </tr>
                </thead>
                <tbody>
                  {filas.map((item, idx) => (
                    <tr
                      key={idx}
                      className="informe-drilldown-row"
                      style={{ cursor: 'pointer' }}
                      title="Clic para ver movimientos del tipo"
                      onClick={() => abrirDrillDownTipoProveedor(item)}
                    >
                      <td>{item.tipo_proveedor}</td>
                      <td>{item.cantidad_proveedores || 0}</td>
                      <td>{item.cantidad_movimientos || 0}</td>
                      <td className="negative">${parseFloat(item.total_egresos || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</td>
                      <td className="positive">${parseFloat(item.total_ingresos || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p style={{ fontSize: '0.85rem', color: '#666', marginTop: 8 }}>
                Haga clic en una fila para ver el detalle de movimientos.
              </p>
            </div>
            {renderGraficos()}
          </div>
        );
      }

      case 'abonos-cargados-cobrados': {
        const resumen = datos || {};
        const pendientes = Array.isArray(resumen.clientes_pendientes) ? resumen.clientes_pendientes : [];
        return (
          <div className="informe-content">
            {resumen.sin_predefinido && (
              <p className="informe-aviso">No se encontró el predefinido &quot;Abono Mensual&quot; en el sistema.</p>
            )}
            <div className="stats-grid">
              <div className="stat-item">
                <label>Abonos cargados</label>
                <span className="negative">${parseFloat(resumen.cargados || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="stat-item">
                <label>Abonos cobrados (recibos)</label>
                <span className="positive">${parseFloat(resumen.cobrados || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="stat-item">
                <label>Diferencia</label>
                <span className={parseFloat(resumen.diferencia || 0) >= 0 ? 'negative' : 'positive'}>
                  ${parseFloat(resumen.diferencia || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                </span>
              </div>
              <div className="stat-item">
                <label>Clientes pendientes</label>
                <span>{resumen.pendientes || 0}</span>
              </div>
            </div>
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Cliente</th>
                    <th>CUIT</th>
                    <th>Cargado</th>
                    <th>Cobrado</th>
                    <th>Pendiente</th>
                  </tr>
                </thead>
                <tbody>
                  {pendientes.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="no-data">Sin clientes con saldo pendiente en el período</td>
                    </tr>
                  ) : (
                    pendientes.map((c) => (
                      <tr key={c.id}>
                        <td>{c.razon_social}</td>
                        <td>{c.cuit || '—'}</td>
                        <td className="negative">${parseFloat(c.cargado || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</td>
                        <td className="positive">${parseFloat(c.cobrado || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</td>
                        <td><strong>${parseFloat(c.pendiente || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</strong></td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );
      }

      case 'cierre-fondos': {
        const resumen = datos?.resumen || {};
        const entidades = datos?.entidades || [];
        return (
          <div className="informe-content">
            <p style={{ marginBottom: 12, color: '#555' }}>
              <strong>Fecha de cierre:</strong> {formatFecha(datos?.fecha)}
            </p>
            <div className="stats-grid">
              <div className="stat-item">
                <label>Entidades</label>
                <span>{resumen.total_entidades || 0}</span>
              </div>
              <div className="stat-item">
                <label>Saldo total al cierre</label>
                <span>{fmtMoneyInforme(resumen.saldo_total_cierre)}</span>
              </div>
              <div className="stat-item">
                <label>Ingresos del día</label>
                <span className="positive">{fmtMoneyInforme(resumen.total_ingresos)}</span>
              </div>
              <div className="stat-item">
                <label>Egresos del día</label>
                <span className="negative">{fmtMoneyInforme(resumen.total_egresos)}</span>
              </div>
            </div>
            {entidades.map((e) => (
              <div key={`${e.tipo_entidad}-${e.id}`} style={{ marginTop: 24 }}>
                <h3 style={{ marginBottom: 8, color: '#2c3e50' }}>
                  {e.tipo_entidad === 'caja' ? 'Caja' : 'Cuenta'}: {e.nombre}
                </h3>
                <div className="stats-grid" style={{ marginBottom: 12 }}>
                  <div className="stat-item">
                    <label>Saldo inicial del día</label>
                    <span>{fmtMoneyInforme(e.saldo_inicial_dia)}</span>
                  </div>
                  <div className="stat-item">
                    <label>Ingresos</label>
                    <span className="positive">{fmtMoneyInforme(e.total_ingresos)}</span>
                  </div>
                  <div className="stat-item">
                    <label>Egresos</label>
                    <span className="negative">{fmtMoneyInforme(e.total_egresos)}</span>
                  </div>
                  <div className="stat-item">
                    <label>Saldo al cierre</label>
                    <span><strong>{fmtMoneyInforme(e.saldo_cierre)}</strong></span>
                  </div>
                </div>
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Fecha</th>
                        <th>Clase</th>
                        <th>Concepto</th>
                        <th>Egreso</th>
                        <th>Ingreso</th>
                        <th>Saldo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(e.movimientos || []).length === 0 ? (
                        <tr>
                          <td colSpan={6} className="no-data">Sin movimientos en la fecha</td>
                        </tr>
                      ) : (
                        e.movimientos.map((m, idx) => (
                          <tr key={idx}>
                            <td>{formatFecha(m.fecha)}</td>
                            <td>{m.clase}</td>
                            <td>{m.concepto}</td>
                            <td className="negative">{m.debito ? fmtMoneyInforme(m.debito) : '—'}</td>
                            <td className="positive">{m.credito ? fmtMoneyInforme(m.credito) : '—'}</td>
                            <td>{fmtMoneyInforme(m.saldo_corrido)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        );
      }

      case 'evolucion':
        return (
          <div className="informe-content">
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Saldo Promedio</th>
                    <th>Saldo Total</th>
                    <th>Clientes Activos</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.isArray(datos) && datos.map((item, idx) => (
                    <tr key={idx}>
                      <td>{item.fecha}</td>
                      <td>${parseFloat(item.saldo_promedio || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</td>
                      <td className={parseFloat(item.saldo_total || 0) >= 0 ? 'positive' : 'negative'}>
                        ${parseFloat(item.saldo_total || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                      </td>
                      <td>{item.clientes_activos || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {renderGraficos()}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="informes pagina-listado pagina-listado--scroll">
      <div className="page-header">
        <h1>Informes</h1>
      </div>

      <div className="informe-filters-panel">
        <div className="informe-filters-row">
          <div className="informe-field informe-field--tipo">
            <label htmlFor="informe-tipo">Tipo de informe</label>
            <select
              id="informe-tipo"
              value={tipoInforme}
              onChange={(e) => { setTipoInforme(e.target.value); setDatos(null); setModalInformeAbierto(false); }}
            >
              <option value="general">Resumen General</option>
              <option value="cliente">Resumen por Cliente</option>
              <option value="tipo">Resumen por Tipo</option>
              <option value="clientes-saldo">Clientes con Saldo</option>
              <option value="movimientos">Movimientos por Período</option>
              <option value="formas-pago">Análisis de Formas de Pago</option>
              <option value="evolucion">Evolución de Saldos</option>
              <option value="movimientos-fondos">Movimientos de fondos (planilla)</option>
              <option value="saldos-fondos">Saldos de fondos</option>
              <option value="fondos-tipo-proveedor">Fondos por tipo de proveedor</option>
              <option value="abonos-cargados-cobrados">Abonos cargados vs cobrados</option>
              <option value="cierre-fondos">Cierre de caja / cuentas</option>
            </select>
          </div>

          {TIPOS_CON_RANGO_FECHA.has(tipoInforme) && (
            <>
              <div className="informe-field informe-field--fecha">
                <label htmlFor="informe-fecha-desde">Desde</label>
                <input
                  id="informe-fecha-desde"
                  type="date"
                  value={fechaDesde}
                  onChange={(e) => setFechaDesde(e.target.value)}
                />
              </div>
              <div className="informe-field informe-field--fecha">
                <label htmlFor="informe-fecha-hasta">Hasta</label>
                <input
                  id="informe-fecha-hasta"
                  type="date"
                  value={fechaHasta}
                  onChange={(e) => setFechaHasta(e.target.value)}
                />
              </div>
            </>
          )}

          {tipoInforme === 'cliente' && (
            <div className="informe-field informe-field--select-lg">
              <label htmlFor="informe-cliente">Cliente</label>
              <select id="informe-cliente" value={clienteId} onChange={(e) => setClienteId(e.target.value)}>
                <option value="">Seleccione un cliente</option>
                {clientes.map(c => (
                  <option key={c.id} value={c.id}>{c.razon_social} - {c.cuit}</option>
                ))}
              </select>
            </div>
          )}

          {(tipoInforme === 'tipo' || tipoInforme === 'clientes-saldo' || tipoInforme === 'movimientos' || tipoInforme === 'evolucion' || tipoInforme === 'abonos-cargados-cobrados') && (
            <div className="informe-field informe-field--select-md">
              <label htmlFor="informe-tipo-cliente">Tipo de cliente</label>
              <select id="informe-tipo-cliente" value={tipoCliente} onChange={(e) => setTipoCliente(e.target.value)}>
                <option value="">Todos</option>
                {tipos.map(t => (
                  <option key={t.id} value={t.id}>{t.nombre}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {tipoInforme === 'fondos-tipo-proveedor' && (
          <div className="informe-field informe-field--select-md">
            <label htmlFor="informe-tipo-proveedor">Tipo de proveedor</label>
            <select
              id="informe-tipo-proveedor"
              value={tipoProveedor}
              onChange={(e) => setTipoProveedor(e.target.value)}
            >
              <option value="">Todos</option>
              {tiposProveedores.map((t) => (
                <option key={t.id} value={t.id}>{t.nombre}</option>
              ))}
            </select>
          </div>
          )}

          {tipoInforme === 'cierre-fondos' && (
            <div className="informe-field informe-field--fecha">
              <label htmlFor="informe-fecha-cierre">Fecha de cierre</label>
              <input
                id="informe-fecha-cierre"
                type="date"
                value={fechaCierre}
                onChange={(e) => setFechaCierre(e.target.value)}
              />
            </div>
          )}

          {(tipoInforme === 'movimientos-fondos' || tipoInforme === 'fondos-tipo-proveedor' || tipoInforme === 'cierre-fondos') && (
          <div className="informe-filters-section">
            <div className="informe-field informe-field--multiselect">
              <label>Cuentas bancarias (multi-select)</label>
              <div className="informe-multiselect">
                {cuentasBancarias.map((cb) => (
                  <label key={cb.id} className="informe-multiselect-option">
                    <input
                      type="checkbox"
                      checked={cuentasSeleccionadas.includes(String(cb.id))}
                      onChange={(e) => {
                        const id = String(cb.id);
                        setCuentasSeleccionadas((prev) =>
                          e.target.checked ? [...prev, id] : prev.filter((x) => x !== id)
                        );
                      }}
                    />
                    <span>{cb.banco_nombre} — {cb.numero_cuenta}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="informe-field informe-field--multiselect">
              <label>Cajas (multi-select)</label>
              <div className="informe-multiselect">
                {cajas.map((c) => (
                  <label key={c.id} className="informe-multiselect-option">
                    <input
                      type="checkbox"
                      checked={cajasSeleccionadas.includes(String(c.id))}
                      onChange={(e) => {
                        const id = String(c.id);
                        setCajasSeleccionadas((prev) =>
                          e.target.checked ? [...prev, id] : prev.filter((x) => x !== id)
                        );
                      }}
                    />
                    <span>{c.nombre}</span>
                  </label>
                ))}
              </div>
            </div>
            <p className="informe-filters-hint">
              {tipoInforme === 'cierre-fondos'
                ? 'Seleccione una o más cajas y/o cuentas para el cierre del día indicado.'
                : 'Deje vacío para incluir todos los movimientos. Seleccione una sola caja o cuenta para saldo corrido y título dinámico.'}
            </p>
          </div>
        )}

        {tipoInforme === 'clientes-saldo' && (
          <div className="informe-filters-section">
            <div className="informe-filters-row">
              <div className="informe-field informe-field--compact">
                <label htmlFor="informe-cond-imp">Cond. impositiva</label>
                <input
                  id="informe-cond-imp"
                  type="text"
                  value={filtrosClientes.condicion_impositiva}
                  onChange={(e) => setFiltrosClientes((p) => ({ ...p, condicion_impositiva: e.target.value }))}
                />
              </div>
              <div className="informe-field informe-field--compact">
                <label htmlFor="informe-iibb">Ingresos brutos</label>
                <input
                  id="informe-iibb"
                  type="text"
                  value={filtrosClientes.ingresos_brutos}
                  onChange={(e) => setFiltrosClientes((p) => ({ ...p, ingresos_brutos: e.target.value }))}
                />
              </div>
              <div className="informe-field informe-field--compact">
                <label htmlFor="informe-drg">DRG</label>
                <input
                  id="informe-drg"
                  type="text"
                  value={filtrosClientes.drg}
                  onChange={(e) => setFiltrosClientes((p) => ({ ...p, drg: e.target.value }))}
                />
              </div>
              <div className="informe-field informe-field--compact">
                <label htmlFor="informe-dv">DV</label>
                <input
                  id="informe-dv"
                  type="text"
                  value={filtrosClientes.dv}
                  onChange={(e) => setFiltrosClientes((p) => ({ ...p, dv: e.target.value }))}
                />
              </div>
              <div className="informe-field informe-field--compact">
                <label htmlFor="informe-iva">IVA líquida</label>
                <select
                  id="informe-iva"
                  value={filtrosClientes.iva_liquida}
                  onChange={(e) => setFiltrosClientes((p) => ({ ...p, iva_liquida: e.target.value }))}
                >
                  <option value="">Todas</option>
                  <option value="1">Sí</option>
                  <option value="0">No</option>
                </select>
              </div>
            </div>
          </div>
        )}

        <div className="informe-filters-actions">
          <button type="button" onClick={handleGenerarInforme} disabled={loading} className="btn-primary">
            {loading ? 'Generando...' : 'Generar informe'}
          </button>
        </div>
      </div>

      {modalInformeAbierto && datos && (
        <div className="informe-modal-overlay" onClick={cerrarModalInforme}>
          <div className="informe-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="informe-modal-titulo">
            <div className="informe-modal-header">
              <h2 id="informe-modal-titulo">{getTituloModalInforme()}</h2>
              {renderAccionesModalInforme()}
            </div>
            <div className="informe-modal-body">
              {renderInforme()}
            </div>
          </div>
        </div>
      )}

      {modalDrillDownAbierto && drillDownTipo && (
        <div className="informe-modal-overlay" onClick={cerrarDrillDown}>
          <div className="informe-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="informe-modal-header">
              <h2>Movimientos — {drillDownTipo.nombre}</h2>
              <div className="informe-modal-actions">
                {drillDownMovimientos.length > 0 && (
                  <ExportActions
                    onCsv={() => exportarDrillDown('csv')}
                    onExcel={() => exportarDrillDown('excel')}
                    showPrint={false}
                  />
                )}
                <button type="button" onClick={cerrarDrillDown} className="btn-secondary">Cerrar</button>
              </div>
            </div>
            <div className="informe-modal-body">
              {drillDownLoading ? (
                <p>Cargando movimientos…</p>
              ) : drillDownMovimientos.length === 0 ? (
                <p>No hay movimientos para este tipo en el período seleccionado.</p>
              ) : (
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Fecha</th>
                        <th>Clase</th>
                        <th>Concepto</th>
                        <th>Proveedor</th>
                        <th>Origen</th>
                        <th>Destino</th>
                        <th>Egreso</th>
                        <th>Ingreso</th>
                      </tr>
                    </thead>
                    <tbody>
                      {drillDownMovimientos.map((r) => (
                        <tr key={r.id}>
                          <td>{formatFecha(r.fecha)}</td>
                          <td>{r.clase}</td>
                          <td>{r.concepto}</td>
                          <td>{r.proveedor}</td>
                          <td>{r.origen}</td>
                          <td>{r.destino}</td>
                          <td className="negative">
                            {r.egreso > 0
                              ? `$${parseFloat(r.egreso).toLocaleString('es-AR', { minimumFractionDigits: 2 })}`
                              : '-'}
                          </td>
                          <td className="positive">
                            {r.ingreso > 0
                              ? `$${parseFloat(r.ingreso).toLocaleString('es-AR', { minimumFractionDigits: 2 })}`
                              : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Informes;
